import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Badge,
  TextField,
  InputAdornment,
  Divider,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import { Search } from 'lucide-react';
import { Plus } from 'lucide-react';
import { useConciergeStore } from '../../store/conciergeStore';
import ConciergeThreadItem from './ConciergeThreadItem';

/**
 * ConciergeThreadList
 * Left-pane list of guest threads for the concierge center.
 * - Pulls data from useConciergeStore
 * - Provides built-in search (q)
 * - Highlights active thread
 */
export default function ConciergeThreadList({
  propertyId: propPropertyId,
  onSelect,          // optional: (thread) => void
  height = '100%',
  autoFocus = false,
}) {
  const {
    threads,
    activeThread,
    setActiveThread,
    fetchThreads,
    loading,
  } = useConciergeStore();

  const propertyId = propPropertyId || localStorage.getItem('activePropertyId');

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Initial & reactive fetch
  useEffect(() => {
    if (!propertyId) return;
    fetchThreads(propertyId, { page: 1, limit: 50, q: debouncedQ }).catch((e) =>
      console.warn('[ConciergeThreadList] fetchThreads error', e)
    );
  }, [propertyId, debouncedQ, fetchThreads]);

  const handleSelect = useCallback((t) => {
    setActiveThread(t);
    if (onSelect) onSelect(t);
  }, [setActiveThread, onSelect]);

  const content = useMemo(() => {
    if (loading && (!threads || threads.length === 0)) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 1 }}>Loading threads…</Typography>
        </Box>
      );
    }

    if (!threads || threads.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3 }}>
          No conversations yet.
        </Typography>
      );
    }

    return (
      <List dense disablePadding sx={{ px: 0 }}>
        {threads.map((t) => (
          <ConciergeThreadItem
            key={t.id || `${t.propertyId}:${t.userUid}`}
            thread={t}
            active={activeThread?.id === t.id}
            onSelect={handleSelect}
          />
        ))}
      </List>
    );
  }, [threads, activeThread, loading, handleSelect]);

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column', bgcolor: '#f5f6f6ff' }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Chats</Typography>
          
        </Box>

      </Box>

      {/* Search */}
      <Box sx={{ px: 2, pb: 1 }}>
        <TextField
          fullWidth
          placeholder="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          size="small"
          autoFocus={autoFocus}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 999,
              backgroundColor: '#ffffffff',
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              transition: 'box-shadow 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              },
              '&.Mui-focused': {
                boxShadow: '0 0 0 2px rgba(108,92,231,0.3), 0 4px 12px rgba(0,0,0,0.12)',
              },
            },
          }}
        />
      </Box>

      

      {/* Thread list */}
      <Box sx={{ overflowY: 'auto', flex: 1, px: 1.5, py: 2 }}>
        <List dense disablePadding sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pt: 0.5,
          pb: 0.5,
          '& > *': {
            borderRadius: 2,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)',
          },
          '& > *:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
          },
          '& .MuiListItemButton-root': {
            borderRadius: 2,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)',
            transition: 'transform 0.04s ease, box-shadow 0.12s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
            },
          },
          '& .Mui-selected': {
            background: 'linear-gradient(180deg, #2D2F39 0%, #1F2027 100%)',
            color: '#fff',
            boxShadow: '0 6px 18px rgba(0,0,0,0.22)'
          },
        }}>
          {threads && threads.length > 0 ? (
            threads.map((t) => (
              <ConciergeThreadItem
                key={t.id || `${t.propertyId}:${t.userUid}`}
                thread={t}
                active={activeThread?.id === t.id}
                onSelect={handleSelect}
                variant="card" // hint for item styling if supported
              />
            ))
          ) : (
            content
          )}
        </List>
      </Box>
    </Box>
  );
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString();
}